// First Created - 07 Oct 2026
// Author - Atul Pandey
// Purpose - To deactivate or hard delete an
//           Org Trail Session Q&A Assignment


import React, {
    useState,
} from "react"


import {
    useNavigate,
} from "react-router"


import useTitle
    from "@/hooks/useTitle"


import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"


import PulseLoader
    from "react-spinners/PulseLoader"


import Modal
    from "@/components/common/modal/Modal"


import {
    useGetOrgTrailSessionQNAAssignmentsQuery,
    useUpdateOrgTrailSessionQNAAssignmentMutation,
    useDeleteOrgTrailSessionQNAAssignmentMutation,
} from "./orgTrailSessionQNAAssignmentsApiSlice"



/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/

const EtDeleteOrgTrailSessionQNAAssignment = ({
    id,
    setActiveAdminView,
}) => {

    useTitle(
        "Delete Session Q&A Assignment"
    )



    /*
    |--------------------------------------------------------------------------
    | 01 - Get Assignment From List Cache
    |--------------------------------------------------------------------------
    */

    const {
        assignment,
        isLoading,
        isFetching,
    } =
        useGetOrgTrailSessionQNAAssignmentsQuery(
            {},
            {
                selectFromResult: ({
                    data,
                    isLoading,
                    isFetching,
                }) => ({

                    assignment:
                        data
                            ?.entities[
                                id
                            ],

                    isLoading,

                    isFetching,

                }),
            }
        )



    /*
    |--------------------------------------------------------------------------
    | 02 - Loading
    |--------------------------------------------------------------------------
    */

    if (
        isLoading ||
        isFetching ||
        !assignment
    ) {

        return (

            <PulseLoader
                color="#ea580c"
            />
        )
    }



    return (

        <EtDeleteOrgTrailSessionQNAAssignmentForm

            assignment={
                assignment
            }

            id={
                id
            }

            setActiveAdminView={
                setActiveAdminView
            }

        />
    )
}



/*
|--------------------------------------------------------------------------
| Delete Form
|--------------------------------------------------------------------------
*/

const EtDeleteOrgTrailSessionQNAAssignmentForm = ({
    assignment,
    id,
    setActiveAdminView,
}) => {

    const navigate =
        useNavigate()



    /*
    |--------------------------------------------------------------------------
    | 03 - Modal State
    |--------------------------------------------------------------------------
    */

    const [
        isModalOpen,
        setIsModalOpen,
    ] =
        useState(
            false
        )


    const [
        actionMode,
        setActionMode,
    ] =
        useState(
            "inactive"
        )



    /*
    |--------------------------------------------------------------------------
    | 04 - Update Mutation
    |--------------------------------------------------------------------------
    */

    const [
        updateOrgTrailSessionQNAAssignment,
        {
            isLoading:
                isUpdating,

            isError:
                isUpdateError,

            error:
                updateError,
        },
    ] =
        useUpdateOrgTrailSessionQNAAssignmentMutation()



    /*
    |--------------------------------------------------------------------------
    | 05 - Hard Delete Mutation
    |--------------------------------------------------------------------------
    */

    const [
        deleteOrgTrailSessionQNAAssignment,
        {
            isLoading:
                isDeleting,

            isError:
                isDeleteError,

            error:
                deleteError,
        },
    ] =
        useDeleteOrgTrailSessionQNAAssignmentMutation()



    const isLoading =
        isUpdating ||
        isDeleting


    const isError =
        isUpdateError ||
        isDeleteError


    const error =
        updateError ||
        deleteError



    /*
    |--------------------------------------------------------------------------
    | 06 - Open Modal
    |--------------------------------------------------------------------------
    */

    const handleOpenModal = (
        mode
    ) => {

        setActionMode(
            mode
        )


        setIsModalOpen(
            true
        )
    }



    /*
    |--------------------------------------------------------------------------
    | 07 - Close Modal
    |--------------------------------------------------------------------------
    */

    const handleCloseModal = () => {

        setIsModalOpen(
            false
        )
    }



    /*
    |--------------------------------------------------------------------------
    | 08 - Return To List
    |--------------------------------------------------------------------------
    */

    const returnToList = () => {

        if (
            setActiveAdminView
        ) {

            setActiveAdminView(
                "orgTrailSessionQNAAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionQNAAssignments"
            )
        }
    }



    /*
    |--------------------------------------------------------------------------
    | 09 - Confirm Action
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async (
            e
        ) => {

            e.preventDefault()


            try {


                /*
                |--------------------------------------------------------------------------
                | A - Hard Delete
                |--------------------------------------------------------------------------
                */

                if (
                    actionMode ===
                    "hard"
                ) {

                    const response =
                        await deleteOrgTrailSessionQNAAssignment({

                            id,

                        })
                            .unwrap()


                    alert(
                        response
                            ?.message ||
                        "Session Q&A assignment permanently deleted"
                    )


                    returnToList()

                    return
                }



                /*
                |--------------------------------------------------------------------------
                | B - Make Inactive
                |--------------------------------------------------------------------------
                */

                const response =
                    await updateOrgTrailSessionQNAAssignment({

                        id,

                        active:
                            false,

                    })
                        .unwrap()


                alert(
                    response
                        ?.message ||
                    "Session Q&A assignment made inactive successfully"
                )


                returnToList()


            } catch (
                submitError
            ) {

                console.error(
                    submitError
                )


                alert(
                    submitError
                        ?.data
                        ?.message ||
                    (
                        actionMode ===
                        "hard"
                            ?
                            "Failed to permanently delete Session Q&A assignment"
                            :
                            "Failed to deactivate Session Q&A assignment"
                    )
                )


            } finally {

                handleCloseModal()
            }
        }



    /*
    |--------------------------------------------------------------------------
    | 10 - Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {

        returnToList()
    }



    /*
    |--------------------------------------------------------------------------
    | 11 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black"
        >

            {
                isLoading &&
                (
                    <PulseLoader
                        color="#ea580c"
                    />
                )
            }


            {
                isError &&
                (
                    <p
                        className="text-orange-700 font-semi-bold py-2"
                    >
                        {
                            error
                                ?.data
                                ?.message
                        }
                    </p>
                )
            }



            <div
                className="bg-white p-1 m-1 rounded"
            >

                <p
                    className="p-1 m-1 font-bold font-poppins text-xl"
                >
                    Delete Session Q&A Assignment
                </p>


                <div
                    className="bg-gray-200 rounded m-1 p-1"
                >

                    <form
                        onSubmit={
                            e => {

                                e.preventDefault()

                                handleOpenModal(
                                    "inactive"
                                )
                            }
                        }

                        className="m-1 p-1 text-xs font-poppins"
                    >


                        {/* Organization */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Organization
                            </p>


                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.orgId
                                        ?.organizationName ||
                                    "Organization not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                        </div>



                        {/* Trail */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Trail
                            </p>


                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.trailId
                                        ?.trailName ||
                                    "Trail not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                        </div>



                        {/* Cohort */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Cohort
                            </p>


                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.cohortId
                                        ?.cohortName ||
                                    assignment
                                        ?.cohortId
                                        ?.cohortCode ||
                                    "Cohort not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                        </div>



                        {/* Session */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session
                            </p>


                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.sessionId
                                        ?.sessionName ||
                                    "Session not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                        </div>



                        {/* Session Q&A Set */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session Q&A Set
                            </p>


                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.sessionQNASetId
                                        ?.sessionQNAName ||
                                    "Session Q&A Set not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />


                            {
                                assignment
                                    ?.sessionQNASetId
                                    ?.sessionQNADesc &&
                                (
                                    <p
                                        className="text-[9px] text-gray-500 m-1 px-1"
                                    >
                                        {
                                            assignment
                                                .sessionQNASetId
                                                .sessionQNADesc
                                        }
                                    </p>
                                )
                            }

                        </div>



                        {/* Current Assignment */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Current Assignment
                            </p>


                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.active !==
                                        false
                                        ?
                                        "Active"
                                        :
                                        "Inactive"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                        </div>



                        {/* Warning */}

                        <div
                            className="bg-orange-50 border border-orange-200 rounded m-1 p-2"
                        >

                            <p
                                className="text-xs font-bold text-orange-700"
                            >
                                Choose how this Q&A assignment should be removed from active use.
                            </p>


                            <p
                                className="text-[10px] text-orange-700 mt-1"
                            >
                                Make Inactive preserves the assignment but stops it from being used for cohort Q&A visibility.
                                Hard Delete permanently removes the assignment.
                            </p>

                        </div>



                        {/* Actions */}

                        <div
                            id="assignmentActions"

                            className="bg-white m-1 px-1 py-3 rounded"
                        >

                            <EtBtnFormBasic
                                name={
                                    "Make Inactive"
                                }

                                type={
                                    "clear"
                                }

                                handleClick={
                                    () =>
                                        handleOpenModal(
                                            "inactive"
                                        )
                                }
                            />


                            <EtBtnFormBasic
                                name={
                                    "Hard Delete"
                                }

                                type={
                                    "create"
                                }

                                handleClick={
                                    () =>
                                        handleOpenModal(
                                            "hard"
                                        )
                                }
                            />


                            <EtBtnFormBasic
                                name={
                                    "Cancel"
                                }

                                type={
                                    "cancel"
                                }

                                handleClick={
                                    handleCancel
                                }
                            />

                        </div>

                    </form>

                </div>

            </div>



            <Modal
                isOpen={
                    isModalOpen
                }

                onClose={
                    handleCloseModal
                }

                onConfirm={
                    handleConfirm
                }
            />

        </div>
    )
}


export default EtDeleteOrgTrailSessionQNAAssignment